import Modal from "@src/components/UIElements/Modal";
import styles from "../../../Tabs/Content/styles/NotesIcon.module.scss";
import Image from "@src/lib/Image";
import { getAwsAssets, hitHapticFeedback } from "@src/utils";
import { useDispatch, useSelector } from "react-redux";
import { showNotesModalAction } from "../../../Tabs/Content/store/actions";
import { CONTENT_TYPE } from "../../../Tabs/Content/store/constants";
import ContentDownloader from "../../../Tabs/Content/components/ContentList/ContentDownloader";
import ContentShimmmer from "../../../Tabs/Content/components/ContentShimmer";
import ListItemDefault from "../../../Tabs/Content/components/ContentList/ListItem";
import { funnelTrackAction } from "../../../Tabs/Overview/store/actions";
import { installmentsAlertStateEnum, trackIDMap } from "../../../Tabs/Overview/store/constants";
import { useRouter } from "@src/lib/router";
import handleOpenContent from "../../../Tabs/Content/helpers/handleOpenContent";
import { showContentLockedBottomSheetAction, showCourseLockedBottomSheetAction, showViewsExhaustedBottomSheetAction } from "../../store/actions";
import ContentActionMenu from "@src/components/Tabs/Content/components/ContentList/ContentActionMenu";
import { t } from "i18next";

const NotesAttachmentModal = (props) => {

  const {
    content,
    getOfflineDownloadedItemProgress,
    getOfflineDownloadedItemState,
    handleGetOfflineDownloadList,
    handleSetCheckProgressInterval,
    handleClearCheckProgressInterval,
    videoMarkedContentIds = new Set(),
    documentMarkedContentIds = new Set(),
    liveClassMarkedContentIds = new Set(),
    enableMarkContentAsDone = false,
  } = props;

  const router = useRouter();
  const dispatch = useDispatch();

  const userId = useSelector((state) => state?.overviewReducer?.userDetails?.responseData?.user?.id);
  const orgId = useSelector((state) => state?.overviewReducer?.overViewDetails?.course?.metaData?.orgId);
  const isCourseContentMarkedExists =  useSelector(
    (state) => state?.courseContentReducer?.markDoneContentIds?.isExisting) || false;

    const isPurchased = useSelector((state) => state?.overviewReducer?.overViewDetails?.course?.metaData?.isPurchased) || 0;

  const renderImage = (format, url) => {
    if (format === "png" || format === "jpeg") {
      return (
        <img
          src={url}
          alt="downArrowIcon"
          height={60}
          width={80}
        />
      );
    } else if (format === "test") {
      return (
        <div className={styles.pdfThumbnail}>
          <Image
            src={getAwsAssets("onlineTestThumbnail.svg", "content")}
            alt="onlineTestIcon"
            height={60}
            width={80}
            eager={true}
            objectFit="contain"
          />
        </div>
      );
    } else {
      return (
        <div className={styles.pdfThumbnail}>
          <Image
            src={getAwsAssets("pdfThumbnail.svg", "content")}
            alt="downArrowIcon"
            height={60}
            width={80}
            eager={true}
            objectFit="contain"
          />
        </div>
      );
    }
  };

  const handleCloseModal = () => {
    dispatch(showNotesModalAction(false));
  };

  const state = useSelector(
    (state) => state?.overviewReducer?.overViewDetails?.course
  );

  const isCourseLocked = useSelector(
    (state) =>
      state?.overviewReducer?.overViewDetails?.course?.installments
        ?.installmentAlertState
  );

  const loadingNotesContent = useSelector(
    (state) => state?.courseContentReducer?.loadingNotesContent
  );

  const handleViewContentItem = (item) => {
    const isTest = item?.mediaType === "test";
    // The video/attachments API returns a payload shape that differs from
    // a regular content item: `contentId` holds the parent content id
    // (while `id` is the attachment's own id) and the test type is exposed
    // as `type` instead of `typeOfTest`. `handleOpenContent` (and the
    // downstream tb-access call for typeOfTest === 2 / realtime tests)
    // reads `id` and `typeOfTest`, so we remap them here before handing
    // the payload off — otherwise the typeOfTest === 2 branch is skipped
    // and the wrong contentId is sent to tb-access.
    const itemPayload = isTest
      ? {
          ...item,
          id: item?.contentId,
          typeOfTest: item?.type,
          URL: item?.url,
          contentType: CONTENT_TYPE.TEST,
        }
      : { ...item, contentType: CONTENT_TYPE.DOCUMENT, format: item?.mediaType };
    handleOpenContent({
      ...itemPayload,
      courseId: +router?.query?.courseId,
      originalCourseId: parseInt(router?.query?.originalCourseId),
    });
  };

  const handleItemClicked = (item) => {
    const isTest = item?.mediaType === "test";
    localStorage.setItem("currentItem", JSON.stringify(isTest ? item : content));
    hitHapticFeedback();
    dispatch(
      funnelTrackAction({
        type: trackIDMap.contentView,
        isPurchased:
          state?.installments?.paidInstallmentSummary?.isPaymentComplete === 1 || 
          state?.metaData?.isPurchased === 1,
        courseId: router?.query?.courseId,
        folderId: router?.query?.folderId || 0,
        contentId: content?.id,
        contentType: isTest ? CONTENT_TYPE.TEST : CONTENT_TYPE.DOCUMENT,
        format: item?.mediaType
      })
    );
    if (isCourseLocked === installmentsAlertStateEnum.COURSE_LOCKED && content?.isLocked) {
      dispatch(showCourseLockedBottomSheetAction(true));
      return;
    }
    if (
      content.isScheduled ||
      (!content.isPurchased && content?.isLocked)
    ) {
      dispatch(showContentLockedBottomSheetAction(true));
    } else handleViewContentItem(item);
  }

  return (
    <Modal
      handleClose={handleCloseModal}
      header={content?.name}
    >
      <div>
        <ListItemDefault
          key={content.id}
          content={content}
          getOfflineDownloadedItemProgress={getOfflineDownloadedItemProgress}
          getOfflineDownloadedItemState={getOfflineDownloadedItemState}
          handleGetOfflineDownloadList={handleGetOfflineDownloadList}
          handleSetCheckProgressInterval={handleSetCheckProgressInterval}
          handleClearCheckProgressInterval={handleClearCheckProgressInterval}
          isCourseContentMarkedExists={isCourseContentMarkedExists}
          videoMarkedContentIds={videoMarkedContentIds}
          documentMarkedContentIds={documentMarkedContentIds}
          liveClassMarkedContentIds={liveClassMarkedContentIds}
          enableMarkContentAsDone={enableMarkContentAsDone}
        />
        {loadingNotesContent && !content?.attachments?.length && (
          <ContentShimmmer />
        )}
        {content?.attachments?.map((item, i) => {
          // Live class recordings ship their attachments inline with the
          // initial content payload, so the modal never has to fire
          // `getContentAttachmentsAction` for them. Non-live videos instead
          // expose only a `hasAttachments: 1` flag and rely on that extra
          // fetch to populate `attachments`. We use that asymmetry to
          // identify live recordings here and restore the legacy permissive
          // flow: PDF attachments on live recordings show the download
          // button regardless of `canDownload`. Every other source continues
          // to gate on `canDownload === 1`.
          const isLiveRecordingAttachment = !content?.hasAttachments;
          const isMarkVisibleIcon = (item?.mediaType === "pdf"  || item?.mediaType === "video") && (isPurchased === 1 || item?.isPurchased === 1);
          const showDownloadForPdf =
            item?.mediaType === "pdf" &&
            (isLiveRecordingAttachment || item?.canDownload === 1);

            const contentId = item?.contentId || item?.id;

            const isContentMarkedDone =  documentMarkedContentIds?.has(contentId) || false;
          
            const payloadData = {
              orgId: orgId,
              courseId: item?.courseId,
              userId: userId || null,
              contentId: item?.contentId,
            }
          
          return (
            <div key={i} className={styles.notesCardWrapper} onClick={()=>handleItemClicked(item)}>
              <div className={styles.thumbnailCol}>
                {renderImage(item?.mediaType, item?.url)}
                {isContentMarkedDone ? (
                  <span className={styles.completed}>
                    <span className={styles.completed__icon} aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="6" cy="6" r="6" fill="currentColor" />
                        <path
                          d="M3.4 6.15L5.15 7.9L8.6 4.2"
                          stroke="#fff"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    {t(
                      "components.tabs.content.components.contentList.listItem.completedText",
                      "Completed"
                    )}
                  </span>
                ) : null}
              </div>
              <p className={styles.name}>{item?.name}</p>
              {showDownloadForPdf && <ContentDownloader
                content={{
                  ...item, 
                  contentType: 3,
                  format: item.mediaType
                }}
                handleGetOfflineDownloadList={handleGetOfflineDownloadList}
                getOfflineDownloadedItemProgress={
                  getOfflineDownloadedItemProgress
                }
                getOfflineDownloadedItemState={getOfflineDownloadedItemState}
                handleSetCheckProgressInterval={handleSetCheckProgressInterval}
                handleClearCheckProgressInterval={handleClearCheckProgressInterval}
              />}

             {/* {isMarkVisibleIcon && <ContentActionMenu
                content={{...item, contentType: 3, format: item?.mediaType}}
                getOfflineDownloadedItemState={getOfflineDownloadedItemState}
                isContentMarkedDone={isContentMarkedDone}
                handleSetCheckProgressInterval={handleSetCheckProgressInterval}
                isCourseContentMarkedExists={isCourseContentMarkedExists}
                payloadData={payloadData}
                handleGetOfflineDownloadList={handleGetOfflineDownloadList}
                getOfflineDownloadedItemProgress={getOfflineDownloadedItemProgress}
                handleClearCheckProgressInterval={handleClearCheckProgressInterval}
                isAttachment={true}
              />} */}
            </div>
            
          );
        })}
      </div>
    </Modal>
  );
};

export default NotesAttachmentModal;
